import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import { useItem, useList } from '../api/queries'
import type { Plant, WorkOrderDetail, WorkOrderList } from '../api/types'
import { useUser } from '../auth/AuthContext'
import { DataTable, type Column } from '../components/DataTable'
import { Card, ErrorState, Field, LoadingState, Modal, PageHeader, PillFilter, Segmented, StatusBadge } from '../components/ui'
import { boardColumns, transitionActions, workOrderStatusLabel, type TransitionAction } from '../features/maintenance/models'
import { FindingsTable } from '../features/tables/tables'
import { formatDate, formatDateTime, humanize } from '../lib/format'
import { useQueryClient } from '@tanstack/react-query'

const PRIORITY_TONE = { urgent: 'danger', high: 'warn', medium: 'info', low: 'neutral' } as const

function Card1({ o }: { o: WorkOrderList }) {
  return (
    <li className="wo-card">
      <Link to={`/work-orders/${o.id}`} className="wo-card__title">{o.title}</Link>
      <span className="mono muted">{o.code} · {o.equipment_codes.join(', ')}</span>
      <span className="badges"><StatusBadge tone={PRIORITY_TONE[o.priority] ?? 'neutral'} label={humanize(o.priority)} />{o.is_overdue ? <StatusBadge tone="danger" label="Vencida" icon="triangle-alert" /> : null}{o.due_on ? <span className="muted">Vence el {formatDate(o.due_on)}</span> : null}</span>
      <span className="muted">{o.assignee_name || 'Sin asignar'}</span>
    </li>
  )
}

export function WorkOrdersPage() {
  const [params] = useSearchParams()
  const plantParam = params.get('plant') ?? ''
  const plantId = /^\d+$/.test(plantParam) ? Number(plantParam) : undefined
  const [view, setView] = useState<'board' | 'list'>('board')
  const [overdue, setOverdue] = useState(false)
  const plant = useItem<Plant>(plantId ? `/api/plants/${plantId}/` : null)
  const list = useList<WorkOrderList>('/api/work-orders/', { plant: plantId, overdue: overdue ? 'true' : undefined, page_size: 200, ordering: 'due_on' })
  const columns: Column<WorkOrderList>[] = [
    { key: 'code', header: 'Orden', cell: (o) => <Link to={`/work-orders/${o.id}`}>{o.code}</Link> },
    { key: 'title', header: 'Título', cell: (o) => o.title },
    { key: 'eq', header: 'Equipo', cell: (o) => o.equipment_codes.join(', '), priority: 'low' },
    { key: 'status', header: 'Estado', cell: (o) => workOrderStatusLabel(o.status) },
    { key: 'pri', header: 'Prioridad', cell: (o) => <StatusBadge tone={PRIORITY_TONE[o.priority] ?? 'neutral'} label={humanize(o.priority)} /> },
    { key: 'due', header: 'Vencimiento', cell: (o) => <>{formatDate(o.due_on)}{o.is_overdue ? <> <StatusBadge tone="danger" label="Vencida" /></> : null}</> },
    { key: 'who', header: 'Responsable', cell: (o) => o.assignee_name || 'Sin asignar', priority: 'low' },
  ]
  return (
    <div className="page">
      <PageHeader title={plantId ? `Órdenes de trabajo de ${plant.data?.name ?? 'la planta'}` : 'Órdenes de trabajo'} actions={<Segmented small label="Vista" value={view} onChange={setView} options={[{ value: 'board', label: 'Tablero' }, { value: 'list', label: 'Lista' }]} />} />
      <div className="pills"><PillFilter active={overdue} onClick={() => setOverdue(!overdue)}>Solo vencidas</PillFilter></div>
      {list.isPending ? <LoadingState /> : list.isError ? <ErrorState error={list.error} onRetry={() => void list.refetch()} /> : view === 'list' ? (
        <Card><DataTable caption="Órdenes de trabajo" columns={columns} rows={list.data.results} rowKey={(o) => o.id} emptyTitle="No hay órdenes de trabajo" /></Card>
      ) : (
        <div role="group" aria-label="Tablero de órdenes de trabajo" className="board">
          {boardColumns(list.data.results).map((c) => (
            <section key={c.status} className="board__col"><h3>{c.label} <span className="muted mono">{c.items.length}</span></h3><ul className="plain">{c.items.map((o) => <Card1 key={o.id} o={o} />)}</ul></section>
          ))}
        </div>
      )}
    </div>
  )
}

function TransitionDialog({ order, action, onClose }: { order: WorkOrderDetail; action: TransitionAction; onClose: () => void }) {
  const client = useQueryClient()
  const assignees = useItem<{ id: number; name: string; username: string; role: string }[]>(action.needsAssignee ? '/api/assignees/' : null)
  const [assignee, setAssignee] = useState('')
  const [notes, setNotes] = useState(order.completion_notes)
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  async function run() {
    setBusy(true)
    setError(null)
    try {
      if (action.to === 'done') await api.patch(`/api/work-orders/${order.id}/`, { completion_notes: notes })
      await api.post(`/api/work-orders/${order.id}/transition/`, { to_status: action.to, note: note || undefined, assignee: action.needsAssignee ? Number(assignee) : undefined })
      await client.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('/api/work-orders') })
      onClose()
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo cambiar el estado.') } finally { setBusy(false) }
  }
  const ready = (!action.needsAssignee || assignee) && (action.to !== 'done' || notes.trim())
  return (
    <Modal title={action.label} onClose={onClose} footer={<><button type="button" className="btn btn--ghost" onClick={onClose}>Volver</button><button type="button" className={action.danger ? 'btn btn--danger' : 'btn btn--primary'} disabled={!ready || busy} onClick={() => void run()}>Confirmar: {action.label}</button></>}>
      <div className="form-stack">
        {action.needsAssignee ? <Field label="Responsable" htmlFor="wo-assignee"><select id="wo-assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)}><option value="">Elegir una persona…</option>{assignees.data?.map((u) => <option key={u.id} value={u.id}>{u.name || u.username} ({humanize(u.role)})</option>)}</select></Field> : null}
        {action.to === 'done' ? <Field label="Notas de cierre" htmlFor="wo-notes" hint="Obligatorias para poder marcar una orden como finalizada."><textarea id="wo-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field> : null}
        <Field label="Nota para el historial (opcional)" htmlFor="wo-note"><input id="wo-note" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        {error ? <p role="alert" className="form-error">{error}</p> : null}
      </div>
    </Modal>
  )
}

export function WorkOrderDetailPage() {
  const { id } = useParams()
  const user = useUser()
  const q = useItem<WorkOrderDetail>(`/api/work-orders/${id}/`)
  const [action, setAction] = useState<TransitionAction | null>(null)
  if (q.isPending) return <LoadingState />
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />
  const o = q.data
  const actions = transitionActions(o)
  void user
  return (
    <div className="page">
      <PageHeader title={o.title} subtitle={<><span className="mono">{o.code}</span> · {o.equipment_codes.join(', ')}</>} actions={<><StatusBadge tone="info" label={workOrderStatusLabel(o.status)} />{o.is_overdue ? <StatusBadge tone="danger" label="Vencida" /> : null}</>} />
      <div className="grid grid--2-1">
        <div className="stack">
          <Card title="Detalles"><p>{o.description || 'Sin descripción.'}</p><dl className="defs"><dt>Prioridad</dt><dd>{humanize(o.priority)}</dd><dt>Vencimiento</dt><dd>{formatDate(o.due_on)}</dd><dt>Responsable</dt><dd>{o.assignee_name || 'Sin asignar'}</dd>{o.completion_notes ? <><dt>Notas de cierre</dt><dd>{o.completion_notes}</dd></> : null}</dl></Card>
          <Card title="Hallazgos"><FindingsTable rows={o.findings} /></Card>
        </div>
        <div className="stack">
          <Card title="Acciones">{actions.length ? <div className="row">{actions.map((a) => <button key={a.to} type="button" className={a.danger ? 'btn btn--danger' : 'btn btn--primary'} onClick={() => setAction(a)}>{a.label}</button>)}</div> : <p className="muted">No hay más acciones disponibles para este rol en esta orden.</p>}</Card>
          <Card title="Historial"><ol className="timeline">{o.history.map((h, i) => <li key={i}><strong>{workOrderStatusLabel(h.to_status)}</strong> <span className="muted">de {workOrderStatusLabel(h.from_status).toLowerCase()} por {h.actor_name} · {formatDateTime(h.at)}</span>{h.note ? <div>{h.note}</div> : null}</li>)}</ol></Card>
        </div>
      </div>
      {action ? <TransitionDialog order={o} action={action} onClose={() => setAction(null)} /> : null}
    </div>
  )
}
